import type { User } from '@/lib/auth-types';
import { STRINGS } from '@/strings/strings';

export interface AccountReturnTarget {
  readonly path: string;
  readonly label: string;
}

/**
 * Sebastian ruling 2026-09-15 round 4 — the /account/password and
 * /account/delete return path depends on which worlds the customer
 * has. Book-only customers came from the book flow and /account is
 * not their home base; audio + both-worlds customers came from
 * /account and it IS their home base.
 *
 *   book only (hasBookWorld && !hasAudioWorld):
 *     back → /book/meine, label "Zurück zum Buch"
 *   audio only OR both worlds OR anonymous fallback:
 *     back → /account, label "← Zurück" (existing strings)
 *
 * The "both worlds" ruling is deliberate: for a customer who has both,
 * we cannot know which surface he came from and /account is the
 * canonical account home. Do not guess.
 *
 * Anonymous fallback (`user === null`) never triggers in practice —
 * ProtectedRoute wraps both routes — but the helper is total so
 * callers do not need a null-branch.
 */
export function getAccountReturnTarget(user: User | null): AccountReturnTarget {
  if (user !== null && user.hasBookWorld && !user.hasAudioWorld) {
    return {
      path: '/book/meine',
      label: STRINGS.account.shared.backToBook,
    };
  }
  return {
    path: '/account',
    label: STRINGS.account.shared.backToAccount,
  };
}
